/**
 * More Scene
 *
 * Three-column layout: SHOWS · CONTACT · ABOUT
 * The ABOUT column conceals a codebreak easter egg that cycles
 * through anagram phrases of "ARE YOU ONE OF US?" on each click,
 * ultimately revealing a discount password.
 */

import type { Scene } from '@/types/scene';
import { scrambleMorph, renderChars } from '@/lib/scramble';

/* ── Codebreak data ──────────────────────────────────────────── */

const CODEBREAK_PHRASES = [
  'ARE YOU ONE OF US?',
  'A FURIOUS ONE, YES?',
  'YOU ARE FOES ON US?',
  'A SERIOUS FOE, YOU?',
  "A FOE— YOU'RE IN USE?",
  'O FEARFUL, YES, ONE?',
];

const FINAL_REVEAL = 'ONE WITH US, YES.';

/* ── State & lifecycle ───────────────────────────────────────── */

interface MoreState {
  codebreakIndex: number;
  timers: ReturnType<typeof setTimeout>[];
  clickHandlers: { el: Element; handler: () => void }[];
  scrambling: boolean;
}

let state: MoreState | null = null;

function handleAboutClick(): void {
  if (!state || state.scrambling) return;

  const textEl = document.querySelector<HTMLElement>('.about-codebreak-text');
  if (!textEl) return;

  const currentIdx = state.codebreakIndex;
  const nextIdx = currentIdx + 1;

  // All anagram phrases exhausted — reveal the final password
  if (nextIdx >= CODEBREAK_PHRASES.length) {
    state.scrambling = true;
    const fromPhrase = CODEBREAK_PHRASES[currentIdx] ?? '';
    scrambleMorph(textEl, fromPhrase, FINAL_REVEAL, state.timers, () => {
      if (!state) return;
      state.scrambling = false;
      // Add the revealed class for special styling
      textEl.closest('.about-codebreak')?.classList.add('revealed');
    });
    state.codebreakIndex = nextIdx;
    return;
  }

  // Morph to the next anagram phrase
  const fromText = CODEBREAK_PHRASES[currentIdx] ?? '';
  const toText = CODEBREAK_PHRASES[nextIdx] ?? '';
  state.scrambling = true;
  scrambleMorph(textEl, fromText, toText, state.timers, () => {
    if (state) state.scrambling = false;
  });
  state.codebreakIndex = nextIdx;
}

/** Ease-out cubic for smooth scroll deceleration */
function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

/** Smooth-scroll the .more-layout container to a target section */
function smoothScrollTo(container: HTMLElement, target: HTMLElement, duration = 800): void {
  const start = container.scrollTop;
  const end = target.offsetTop;
  const distance = end - start;
  let startTime: number | null = null;

  // Disable snap so it doesn't fight the animation
  container.style.scrollSnapType = 'none';

  function step(timestamp: number): void {
    startTime ??= timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    container.scrollTop = start + distance * easeOutCubic(progress);
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      // Re-enable snap after animation completes
      container.style.scrollSnapType = '';
    }
  }

  requestAnimationFrame(step);
}

export function initMore(): void {
  const clickHandlers: MoreState['clickHandlers'] = [];

  state = {
    codebreakIndex: 0,
    timers: [],
    clickHandlers,
    scrambling: false,
  };

  // Render initial phrase with per-character spans + opacity
  const textEl = document.querySelector<HTMLElement>('.about-codebreak-text');
  if (textEl) {
    const phrase = CODEBREAK_PHRASES[0] ?? '';
    const chars = phrase.split('').map((c) => ({
      current: c,
      target: c,
      resolved: true,
    }));
    renderChars(textEl, chars);
  }

  // Codebreak click
  const aboutCard = document.querySelector<HTMLElement>('.more-card-about');
  if (aboutCard) {
    const handler = (): void => {
      handleAboutClick();
    };
    aboutCard.addEventListener('click', handler);
    clickHandlers.push({ el: aboutCard, handler });
  }

  // Rune nav: up/down between sections
  const scrollContainer = document.querySelector<HTMLElement>('.more-layout');
  const sections = document.querySelectorAll<HTMLElement>('.more-section');
  const runeUp = document.querySelector<HTMLElement>('.more-rune-up');
  const runeDown = document.querySelector<HTMLElement>('.more-rune-down');

  function getCurrentIndex(): number {
    if (!scrollContainer || sections.length === 0) return 0;
    const scrollTop = scrollContainer.scrollTop;
    const sectionHeight = scrollContainer.clientHeight;
    return Math.round(scrollTop / sectionHeight);
  }

  function updateRuneVisibility(): void {
    const idx = getCurrentIndex();
    if (runeUp) runeUp.style.opacity = idx === 0 ? '0.2' : '1';
    if (runeDown) runeDown.style.opacity = idx >= sections.length - 1 ? '0.2' : '1';
  }

  if (runeUp) {
    const handler = (): void => {
      const idx = getCurrentIndex();
      const target = sections[idx - 1];
      if (idx > 0 && scrollContainer && target) {
        smoothScrollTo(scrollContainer, target);
      }
    };
    runeUp.addEventListener('click', handler);
    clickHandlers.push({ el: runeUp, handler });
  }

  if (runeDown) {
    const handler = (): void => {
      const idx = getCurrentIndex();
      const target = sections[idx + 1];
      if (idx < sections.length - 1 && scrollContainer && target) {
        smoothScrollTo(scrollContainer, target);
      }
    };
    runeDown.addEventListener('click', handler);
    clickHandlers.push({ el: runeDown, handler });
  }

  // Update rune visibility on scroll
  if (scrollContainer) {
    const scrollHandler = (): void => {
      updateRuneVisibility();
    };
    scrollContainer.addEventListener('scroll', scrollHandler);
    clickHandlers.push({ el: scrollContainer, handler: scrollHandler });
    updateRuneVisibility();
  }
}

export function destroyMore(): void {
  if (!state) return;
  for (const t of state.timers) clearTimeout(t);
  for (const { el, handler } of state.clickHandlers) {
    el.removeEventListener('click', handler);
  }
  state = null;
}

/* ── Scene definition ────────────────────────────────────────── */

export const more: Scene = {
  id: 'more',
  name: 'More',
  atmosphere: {
    background: 'linear-gradient(180deg, #0a0a0f 0%, #0f1218 100%)',
    fogIntensity: 0.3,
    noiseHue: 220,
    noiseBrightness: 0.6,
    noiseOpacity: 0.45,
  },
  interactions: [
    {
      id: 'back-home',
      target: '[data-interaction="back-home"]',
      action: { type: 'navigate', sceneId: 'home' },
    },
    {
      id: 'nav-music',
      target: '[data-interaction="nav-music"]',
      action: { type: 'navigate', sceneId: 'music' },
    },
    {
      id: 'nav-drip',
      target: '[data-interaction="nav-drip"]',
      action: { type: 'navigate', sceneId: 'drip' },
    },
    {
      id: 'nav-more',
      target: '[data-interaction="nav-more"]',
      action: { type: 'navigate', sceneId: 'more' },
    },
  ],
  transitions: [
    { to: 'home', effect: 'fade', duration: 800 },
    { to: 'music', effect: 'fade', duration: 800 },
    { to: 'drip', effect: 'fade', duration: 800 },
    { to: 'more', effect: 'fade', duration: 800 },
  ],
  render: () => `
    
    <div class="more-layout">

      <!-- Rune navigation -->
      <div class="more-rune-nav">
        <button class="more-rune more-rune-up" aria-label="Previous section">ᛚ</button>
        <button class="more-rune more-rune-down" aria-label="Next section">ᚨ</button>
      </div>

      <!-- ABOUT section -->
      <section id="section-about" class="more-section more-card-about" role="button" tabindex="0" aria-label="Click to reveal secrets">
        <div class="more-card-body about-codebreak">
          <span class="about-codebreak-text">${(CODEBREAK_PHRASES[0] ?? '')
            .split(' ')
            .map((w) => `<span class="cb-word">${w}</span>`)
            .join('')}</span>
        </div>
      </section>

      <!-- SHOWS section -->
      <section id="section-shows" class="more-section">
        <div class="more-card-body">
          <p class="shows-message">WATCHING YOU<br>WATCHING US.</p>
        </div>
      </section>

      <!-- CONTACT section -->
      <section id="section-contact" class="more-section">
        <div class="more-card-body">
          <form class="contact-form" onsubmit="return false;">
            <input type="email" class="contact-input" placeholder="EMAIL" autocomplete="email" />
            <input type="text" class="contact-input" placeholder="SUBJECT" />
            <textarea class="contact-textarea" placeholder="MESSAGE" rows="6"></textarea>
            <button type="submit" class="contact-submit">SEND</button>
          </form>
        </div>
      </section>

    </div>
  `,
};
